import React, { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import workspaceService from "src/services/workspaceService";

export default function OrganisationLinkCard({ workspaces, onLinked }) {
  const legacy = workspaces.filter(
    (workspace) => workspace.can_link_organisation,
  );
  const [organisations, setOrganisations] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [organisationId, setOrganisationId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const hasLegacy = legacy.length > 0;
  useEffect(() => {
    if (!hasLegacy) return;
    let cancelled = false;
    workspaceService
      .organisationConnections()
      .then((response) => {
        if (!cancelled) setOrganisations(response.data?.data || []);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            "Could not load your organisations. Refresh this page to retry.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [hasLegacy]);
  if (!hasLegacy) return null;

  const link = async () => {
    setSaving(true);
    setError("");
    try {
      await workspaceService.linkOrganisation(workspaceId, organisationId);
      setConfirmed(false);
      await onLinked();
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          "Could not link this workspace. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="rounded-2xl border border-theme-200 bg-theme-50/50 p-5 dark:border-dark-border dark:bg-dark-card">
      <div className="flex items-center gap-2 text-primary dark:text-dark-primary">
        <Building2 size={18} />
        <h3 className="font-semibold">
          Bring your existing WhatsApp workspace into your organisation
        </h3>
      </div>
      <p className="mt-2 text-sm text-text-secondary dark:text-dark-text-secondary">
        Link once to let your Auto Appy team use the accounts, numbers and
        conversations already here.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-medium">
          Existing WhatsApp workspace
          <select
            aria-label="Existing WhatsApp workspace"
            disabled={saving}
            value={workspaceId}
            onChange={(event) => {
              setWorkspaceId(event.target.value);
              setConfirmed(false);
            }}
            className="mt-2 w-full rounded-lg border border-border-light bg-card-bg p-3 dark:border-dark-border dark:bg-dark-surface"
          >
            <option value="">Choose a workspace</option>
            {legacy.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium">
          Auto Appy organisation
          <select
            aria-label="Auto Appy organisation"
            disabled={saving}
            value={organisationId}
            onChange={(event) => {
              setOrganisationId(event.target.value);
              setConfirmed(false);
            }}
            className="mt-2 w-full rounded-lg border border-border-light bg-card-bg p-3 dark:border-dark-border dark:bg-dark-surface"
          >
            <option value="">Choose an organisation</option>
            {organisations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="mt-4 flex items-start gap-2 text-sm text-text-secondary dark:text-dark-text-secondary">
        <input
          type="checkbox"
          className="mt-1"
          disabled={saving}
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        Use this organisation’s WhatsApp team for access. People outside that
        team will lose access to this workspace.
      </label>
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={link}
        disabled={saving || !confirmed || !workspaceId || !organisationId}
        className="mt-4 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
      >
        {saving ? "Linking…" : "Link organisation"}
      </button>
    </section>
  );
}
